// Pure softphone presentation helpers shared by the sidebar widget and the
// mobile dock, so both read the runtime the same way.
import { isPooledAgentLine } from '@/lib/call-utils';
import { humanizeLabel } from '@/lib/format';
import type { Contact } from '@/types/contacts';
import type { CallSummary, SoftphoneState } from '@/types/telephony';
import type { ConnectionQuality } from '@/lib/connection-quality';

export type SoftphoneTone = 'success' | 'warning' | 'danger' | 'neutral';

export function statusTone(phase: string): SoftphoneTone {
  if (phase === 'online' || phase === 'in_call' || phase === 'hot_transfer_pending') return 'success';
  if (phase === 'ringing' || phase === 'calling' || phase === 'connecting') return 'warning';
  if (phase === 'error' || phase === 'offline' || phase === 'disabled') return 'danger';
  return 'neutral';
}

export function statusLabel(phase: string): string {
  if (phase === 'in_call') return 'In call';
  if (phase === 'hot_transfer_pending') return 'Online';
  if (phase === 'calling') return 'Dialing';
  if (phase === 'online') return 'Online';
  if (phase === 'connecting') return 'Connecting';
  if (phase === 'ringing') return 'Ringing';
  if (phase === 'disabled') return 'Offline';
  if (phase === 'error') return 'Error';
  return humanizeLabel(phase);
}

export interface PhoneStatus {
  label: string;
  tone: SoftphoneTone;
  pulse: boolean;
  // Set once the phone has been unregistered past the grace window: what the
  // agent should know, next to a Reconnect action.
  notConnectedHint: string | null;
  // Set once stuck-media rebuilds have run out: the line is up but the agent's
  // audio is not reaching the switch. Kept apart from notConnectedHint, whose
  // Reconnect action rebuilds the phone and would drop a pool line.
  mediaHint: string | null;
}

export const MEDIA_FAILED_HINT = "Your audio isn't connecting. Check your internet or switch networks, then leave and rejoin the pool.";
// A bridged call sending the switch no audio (see lib/silent-uplink.ts). Shown
// beside the call, never in place of it: a flapping mic must not flip the
// line's header back and forth.
export const SILENT_UPLINK_HINT = "The prospect can't hear you. Check your mic and your connection.";

// The phone's one status line. The registration clock outranks the phase: the
// retry loop's churn under an outage is not news, and a blip inside the grace
// window never reaches the agent.
export function phoneStatus(phase: string, reconnecting: boolean, quality: ConnectionQuality, mediaFailed = false): PhoneStatus {
  if (reconnecting || (quality.registrationDown && quality.call)) {
    return { label: 'Reconnecting · call still active', tone: 'warning', pulse: true, notConnectedHint: null, mediaHint: null };
  }
  if (quality.registrationDown) {
    const hint = quality.level === 'offline'
      ? 'Your browser is offline. Check your Wi-Fi or ethernet; the phone reconnects on its own.'
      : quality.registrationRetrying
        ? 'The phone cannot reach the call server and keeps retrying on its own.'
        : 'The phone cannot reach the call server and nothing is retrying.';
    return { label: 'Not connected', tone: 'danger', pulse: false, notConnectedHint: hint, mediaHint: null };
  }
  if (mediaFailed) {
    return { label: 'Audio not connecting', tone: 'danger', pulse: false, notConnectedHint: null, mediaHint: MEDIA_FAILED_HINT };
  }
  const weak = (phase === 'online' || phase === 'in_call') && quality.level === 'poor';
  return {
    label: weak ? `${statusLabel(phase)} · weak connection` : statusLabel(phase),
    tone: weak ? 'warning' : statusTone(phase),
    pulse: phase === 'connecting' || phase === 'calling' || phase === 'ringing',
    notConnectedHint: null,
    mediaHint: null,
  };
}

export function formatPhoneDisplay(raw: string): string {
  const trimmed = String(raw || '').trim();
  const us = trimmed.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  if (us) return `+1 (${us[1]}) ${us[2]}-${us[3]}`;
  return trimmed;
}

export function contactTitle(contact: Pick<Contact, 'name' | 'companyName'> | null): string {
  if (!contact) return '';
  return contact.companyName?.trim() || contact.name?.trim() || '';
}

export function contactInitials(contact: Pick<Contact, 'name' | 'companyName'>): string {
  const source = contactTitle(contact);
  const words = source.split(/\s+/).filter(Boolean);
  if (!words.length) return '?';
  return ((words[0][0] || '') + (words[1]?.[0] || '')).toUpperCase();
}

/**
 * The phase the phone UI shows. A tracked call that hasn't ended overrides a
 * runtime that still reads `online`; transport phases (connecting/ringing/
 * calling/error) win over everything.
 */
export function resolveActivePhase(
  state: Pick<SoftphoneState, 'phase'> | undefined,
  activeCall: Pick<CallSummary, 'status'> | null
): string {
  if (!state) return 'offline';
  if (state.phase === 'error') return 'error';
  if (state.phase === 'connecting' || state.phase === 'ringing' || state.phase === 'calling') return state.phase;
  if (activeCall && activeCall.status !== 'ended') {
    return activeCall.status === 'bridged' ? 'in_call' : 'calling';
  }
  if (state.phase === 'online' || state.phase === 'in_call' || state.phase === 'hot_transfer_pending') return state.phase;
  return 'offline';
}

/**
 * On a pooled line, the bridged contact leg this agent is talking to — null
 * while the line is parked waiting for the next call. A leg in the short-call
 * hold stays "bridged" until it hangs up, but the agent has already left it
 * (and may be on the next contact).
 */
export function poolContactLeg(recentCalls: CallSummary[], userId: string | undefined): CallSummary | null {
  return (
    recentCalls.find(
      (call) =>
        call.source === 'pooled_dialer'
        && Boolean(call.campaignContactId)
        && call.status === 'bridged'
        && call.initiatedByUserId === userId
        && !call.shortCallGuardApplied
    ) ?? null
  );
}

/**
 * The conversation an unload of this page would drop, or null. A pool line
 * counts only while a contact is bridged onto it: parked it carries no
 * conversation (a pooled setter holds one all shift), and in wrap-up the
 * contact leg has already ended.
 */
export function liveConversationCall(activeCall: CallSummary | null, recentCalls: CallSummary[]): CallSummary | null {
  if (!activeCall || activeCall.status === 'ended') return null;
  if (!isPooledAgentLine(activeCall)) return activeCall;
  if (!activeCall.initiatedByUserId) return null;
  return poolContactLeg(recentCalls, activeCall.initiatedByUserId);
}

/**
 * The remote party's number. For inbound calls that's the caller (fromPhone);
 * destination/toPhone hold the agent's own DID.
 */
export function remotePartyNumber(
  activeCall: Pick<CallSummary, 'source' | 'fromPhone' | 'destination' | 'toPhone'> | null,
  state: Pick<SoftphoneState, 'remoteIdentity'> | undefined,
  fallback = ''
): string {
  if (activeCall?.source === 'inbound') {
    return activeCall.fromPhone || state?.remoteIdentity || '';
  }
  return activeCall?.destination || activeCall?.toPhone || fallback || state?.remoteIdentity || '';
}
